import { Button, Form, Input, Select, Space } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { keycloak } from "../../..";
import { useAppSelector } from "../../../app/store";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import SuccessModal from "../../../components/common/SuccessModal";
import CustomModal from "../../../components/ui/CustomModal";
import Loader from "../../../components/ui/Loader";
import { useGetAllCompanyQuery } from "../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useCreatePortalDepartmentMutation,
  useGetProtalSingleDepartmentQuery,
  useUpdatePortalDepartmentMutation,
} from "../../../features/portal/settings/settingsApi";
import { useGetAllUsersQuery } from "../../../features/portal/user/userApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import { capitalize } from "../../../utils/string";

export interface ModalConfirmProps<
  DataT = never,
  TypeT =
    | "SUCCESS"
    | "ADD"
    | "UPDATE"
    | "VIEW"
    | "CONFIRM"
    | "CONFIRM_FOR_APPROVAL"
> {
  open: boolean;
  action?: boolean;
  buttonBgDeep?: boolean;
  handleClose: (val?: any) => void;
  type?: TypeT;
  data?: any;
  title?: ReactNode;
  subTitle?: ReactNode;
  buttonName?: string;
  loading?: boolean;
  children?: ReactNode;
  isViewOpen?: boolean;
}

export default function DepartmentAddModal({
  title,
  action,
  buttonName,
  buttonBgDeep,
  subTitle,
  handleClose,
  children,
  type,
  data,
  loading,
  isViewOpen,
  ...rest
}: ModalConfirmProps) {
  const [form] = Form.useForm();
  const {
    open: successOpen,
    handleOpen: handleSuccessOpen,
    handleClose: handleSuccessClose,
  } = useModal();
  const { user, role } = useAppSelector((state) => state.auth);
  const isAdmin2 = keycloak?.realmAccess?.roles?.includes("admin2");
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    number | undefined
  >(role === "SUPER_ADMIN" ? undefined : user?.data?.company_id);
  const [createPortalDepartment, { isLoading, isError, error, isSuccess }] =
    useCreatePortalDepartmentMutation();

  const [
    updatePortalDepartment,
    {
      isLoading: updatePortalDepartmentIsLoading,
      isError: updatePortalDepartmentIsError,
      error: updatePortalDepartmentError,
      isSuccess: updatePortalDepartmentIsSuccess,
    },
  ] = useUpdatePortalDepartmentMutation();

  const {
    data: getProtalSingleDepartmentData,
    isError: getProtalSingleDepartmentIsError,
    error: getProtalSingleDepartmentError,
    isLoading: getProtalSingleDepartmentIsLoading,
  } = useGetProtalSingleDepartmentQuery(data?.id, { skip: !data?.id });

  const {
    isError: isAllCompanyError,
    error: allCompanyError,
    data: allCompanyData,
    isLoading: isAllCompanyLoading,
  } = useGetAllCompanyQuery({
    params: {
      company_id: role === "SUPER_ADMIN" ? undefined : user?.data?.company_id,
      page: 1,
      limit: 1000000000000,
    },
  });
  const {
    isError: getAllUserIsError,
    error: getAllUserError,
    isLoading: getAllUserIsLoading,
    data: getAllUserData,
  } = useGetAllUsersQuery({
    params: {
      company_id: selectedCompanyId,
      skip: undefined,
      page: 1,
      limit: 1000000000000,
    },
  });
  useError(isError, error, "Department create failed");
  useError(
    updatePortalDepartmentIsError,
    updatePortalDepartmentError,
    "Department update failed"
  );
  useError(
    getProtalSingleDepartmentIsError,
    getProtalSingleDepartmentError,
    "Single department get data failed"
  );
  useError(isAllCompanyError, allCompanyError, "Get all company fail");
  useError(getAllUserIsError, getAllUserError, "Get all user fail");

  const onFinish = (values: any) => {
    const newData = {
      name: values?.name,
      head_of_department: values?.head_of_department,
    };
    if (data?.id) {
      updatePortalDepartment({
        data: newData,
        id: data?.id,
      });
    } else {
      createPortalDepartment(newData);
    }
  };

  const handleOkay = () => {
    handleClose();
    handleSuccessClose();
  };

  useEffect(() => {
    if (data?.id && getProtalSingleDepartmentData?.data) {
      form.setFieldsValue({
        name: getProtalSingleDepartmentData?.data?.name,
        head_of_department:
          getProtalSingleDepartmentData?.data?.headOfDepartment?.id,
      });
    }
  }, [data?.id, getProtalSingleDepartmentData?.data, form]);

  useEffect(() => {
    if (isSuccess) {
      form.resetFields();
      handleSuccessOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useEffect(() => {
    if (updatePortalDepartmentIsSuccess) {
      form.resetFields();
      handleSuccessOpen({ type: "ADD" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatePortalDepartmentIsSuccess]);

  return (
    <>
      {successOpen?.type === "ADD" && (
        <SuccessModal
          open={successOpen?.open}
          handleClose={handleSuccessClose}
          handleOkay={handleOkay}
          data={successOpen?.data}
          title={`${
            data?.id
              ? "Department Edited Successfully"
              : "New Department Added Successfully"
          }`}
        />
      )}

      <CustomModal
        {...rest}
        handleClose={handleClose}
        width={500}
        footer={null}
      >
        {data?.id && getProtalSingleDepartmentIsLoading ? (
          <Loader />
        ) : (
          <div>
            <div className="justify-start text-zinc-700 text-xl font-semibold mb-4">
              {isViewOpen ? (
                "View Department"
              ) : (
                <>{data?.id ? "Edit Department" : "Add New Department"}</>
              )}
            </div>

            <Form
              className="custom-form"
              layout="vertical"
              requiredMark="optional"
              size="large"
              onFinish={onFinish}
              form={form}
              initialValues={{
                company_id:
                  role === "SUPER_ADMIN"
                    ? allCompanyData?.data?.companies?.[0]?.id
                    : user?.data?.company_id,
              }}
            >
              <Form.Item
                label="Department Name"
                name="name"
                rules={[
                  { required: true, message: "Please enter department name" },
                ]}
              >
                <Input
                  placeholder="Enter Department Name"
                  allowClear
                  disabled={isViewOpen}
                />
              </Form.Item>

              <Form.Item
                label="Parent Company"
                name="company_id"
                rules={[
                  { required: true, message: "Please enter a company name" },
                ]}
              >
                <Select
                  placeholder="Company Name"
                  showSearch
                  options={
                    allCompanyData?.data?.companies?.map((company: any) => ({
                      label: company?.company_name,
                      value: company?.id,
                    })) || []
                  }
                  onChange={(value) => setSelectedCompanyId(value)}
                  className="w-full"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                  allowClear
                  disabled={isAdmin2}
                  optionFilterProp="label"
                  loading={isAllCompanyLoading}
                />
              </Form.Item>

              <Form.Item
                label="Head of Department"
                name="head_of_department"
                rules={[
                  {
                    required: true,
                    message: "Please enter head of department",
                  },
                ]}
              >
                <Select
                  placeholder="Head of Department"
                  showSearch
                  options={
                    getAllUserData?.data?.users?.map((user: any) => ({
                      label: capitalize(user?.username),
                      value: user?.id,
                    })) || []
                  }
                  disabled={!selectedCompanyId || isViewOpen}
                  className="w-full"
                  suffixIcon={<SelectDownIcon />}
                  style={{ width: "100%" }}
                  allowClear
                  optionFilterProp="label"
                  loading={getAllUserIsLoading}
                />
              </Form.Item>

              <Space className="w-full mt-2" size={12}>
                <Button
                  onClick={handleClose}
                  className="normal-dark-button modal-button"
                  size="middle"
                >
                  Cancel
                </Button>
                {isViewOpen ? (
                  <></>
                ) : (
                  <Button
                    className="modal-button"
                    type="primary"
                    htmlType="submit"
                    size="middle"
                    loading={isLoading || updatePortalDepartmentIsLoading}
                  >
                    {data?.id ? "Update" : "Save"}
                  </Button>
                )}
              </Space>
            </Form>
          </div>
        )}
      </CustomModal>
    </>
  );
}
